import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface ReviewSubmissionData {
  service_provider_id: string;
  service_id: string | null;
  service_request_id: string | null;
  customer_name: string;
  customer_email: string;
  review_text: string;
  rating: number;
}

interface ServiceRequestData {
  id: string;
  user_id: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  status: string;
  services: {
    id: string;
    name: string;
    user_id: string;
  } | null;
  profiles: {
    company_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

const CustomerReviewSubmission = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serviceRequestData, setServiceRequestData] = useState<ServiceRequestData | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get token from URL params for validation
  const token = searchParams.get('token');

  const fetchServiceRequestData = useCallback(async () => {
    try {
      setInitialLoading(true);
      
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          user_id,
          customer_name,
          customer_email,
          customer_phone,
          status,
          services:service_id (
            id,
            name,
            user_id
          )
        `)
        .eq('id', requestId)
        .single();

      // Fetch profile data separately
      let profileData = null;
      if (data?.user_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('company_name, first_name, last_name')
          .eq('id', data.user_id)
          .single();
        profileData = profile;
      }

      if (error) {
        console.error('Error fetching service request:', error);
        setError('Could not find the service request. The link may be invalid or expired.');
        return;
      }

      if (data.status !== 'completed') {
        setError('Reviews can only be submitted for completed services.');
        return;
      }

      setServiceRequestData({
        ...data,
        profiles: profileData
      });
      setCustomerName(data.customer_name || '');
      setCustomerEmail(data.customer_email || '');
      
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while loading the review form.');
    } finally {
      setInitialLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (requestId && token) {
      fetchServiceRequestData();
    } else {
      setError('Invalid review link. Missing request ID or token.');
      setInitialLoading(false);
    }
  }, [requestId, token, fetchServiceRequestData]);

  const handleStarClick = (starIndex: number) => {
    setRating(starIndex + 1);
  };

  const handleStarHover = (starIndex: number) => {
    setHoverRating(starIndex + 1);
  };

  const handleStarLeave = () => {
    setHoverRating(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rating) {
      toast({
        variant: 'destructive',
        title: 'Rating Required',
        description: 'Please select a star rating before submitting.',
      });
      return;
    }

    if (!reviewText.trim()) {
      toast({
        variant: 'destructive',
        title: 'Review Required',
        description: 'Please write a review before submitting.',
      });
      return;
    }

    if (!customerName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Name Required',
        description: 'Please enter your name before submitting.',
      });
      return;
    }

    setLoading(true);

    try {
      const reviewData: ReviewSubmissionData = {
        service_provider_id: serviceRequestData.user_id,
        service_id: serviceRequestData.services?.id || null,
        service_request_id: requestId || null,
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim() || null,
        review_text: reviewText.trim(),
        rating: rating,
      };

      const { error } = await supabase
        .from('reviews')
        .insert([reviewData]);

      if (error) {
        console.error('Error submitting review:', error);
        toast({
          variant: 'destructive',
          title: 'Submission Failed',
          description: 'Failed to submit your review. Please try again.',
        });
        return;
      }

      setSubmitted(true);
      toast({
        title: 'Review Submitted',
        description: 'Thank you for your feedback! Your review has been submitted successfully.',
      });

    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        variant: 'destructive',
        title: 'Submission Failed',
        description: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, index) => {
          const isFilled = index < (hoverRating || rating);
          return (
            <Star
              key={index}
              className={`w-8 h-8 cursor-pointer transition-colors ${
                isFilled 
                  ? 'text-yellow-400 fill-current' 
                  : 'text-gray-300 hover:text-yellow-200'
              }`}
              onClick={() => handleStarClick(index)}
              onMouseEnter={() => handleStarHover(index)}
              onMouseLeave={handleStarLeave}
            />
          );
        })}
        <span className="ml-2 text-sm text-gray-600">
          {rating > 0 && `${rating} out of 5 stars`}
        </span>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gebeya-pink mx-auto mb-4"></div>
          <p className="text-gray-600">Loading review form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Unable to Load Review Form</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Review Submitted!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for taking the time to share your feedback. Your review helps us improve our services.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
            >
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const providerName = serviceRequestData?.profiles?.company_name || 
                      `${serviceRequestData?.profiles?.first_name || ''} ${serviceRequestData?.profiles?.last_name || ''}`.trim() ||
                      'Service Provider';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-800">
              Share Your Experience
            </CardTitle>
            <p className="text-gray-600 mt-2">
              How was your experience with <span className="font-semibold">{providerName}</span>?
            </p>
            {serviceRequestData?.services?.name && (
              <p className="text-sm text-gray-500">
                Service: {serviceRequestData.services.name}
              </p>
            )}
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Star Rating */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Rating *</Label>
                <div className="flex flex-col items-start space-y-2">
                  {renderStars()}
                  <p className="text-sm text-gray-500">
                    Click on the stars to rate your experience
                  </p>
                </div>
              </div>

              {/* Review Text */}
              <div className="space-y-2">
                <Label htmlFor="review" className="text-base font-medium">
                  Your Review *
                </Label>
                <Textarea
                  id="review"
                  placeholder="Please share details about your experience..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={5}
                  className="resize-none"
                  required
                />
                <p className="text-sm text-gray-500">
                  {reviewText.length}/1000 characters
                </p>
              </div>

              {/* Customer Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-medium">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base font-medium">
                    Email Address (Optional)
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !rating || !reviewText.trim() || !customerName.trim()}
                className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white py-3"
              >
                {loading ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerReviewSubmission; 